import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Droplets,
  ShieldCheck,
  Snowflake,
  Waves,
  ScanLine,
  Recycle,
  Anchor,
  Thermometer,
  ChevronDown,
  Car,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { findVehicle, RECOMMENDED } from "@/lib/vehicles";
import { useVehicleState } from "@/lib/vehicleStorage";
import { VehicleSelector } from "@/components/VehicleSelector";
import { MaskedLines } from "@/components/MaskedLines";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";

const BENEFIT_ICONS = [Droplets, ShieldCheck, Snowflake, Waves];
const WHY3_ICONS = [Recycle, Anchor, Thermometer];

export default function Home() {
  const { t, lang } = useLang();
  const [featured, setFeatured] = useState([]);
  const [finder, setFinder] = useVehicleState();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 140]);
  const bgOpacity = useTransform(scrollY, [0, 600], [1, 0.25]);

  const found = findVehicle(finder.marque, finder.modele);
  const finderVehicle = found ? `${finder.marque} ${finder.modele}${finder.annee ? ` ${finder.annee}` : ""}` : "";
  const findMatch = () => {
    const target = found ? RECOMMENDED[found.category].id : "nordiguard-sur-mesure";
    navigate(`/produit/${target}`);
  };

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.slice(0, 1))).catch(() => {});
  }, []);

  const p = featured[0];

  return (
    <main>
      <Seo
        title="NordiGuard — Tapis d'hiver sur-mesure pour voiture"
        description={t("home.sub")}
      />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="grain relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: bgY, opacity: bgOpacity }} className="absolute inset-0">
          <img
            src="/images/hero-hiver.png"
            alt="Tapis d'hiver NordiGuard dans un habitacle de voiture"
            className="h-[115%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05080E] via-[#05080E]/75 to-[#05080E]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05080E] via-transparent to-[#05080E]/60" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-36 pb-24 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-500/25 bg-sky-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-sky-300"
          >
            <Snowflake size={13} /> {t("home.badge")}
          </motion.p>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLines delay={0.25} lines={[t("home.l1"), t("home.l2"), t("home.l3")]} lineClassName="text-[#F8FAFC]" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            {t("home.sub")}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-4 inline-flex items-center gap-2.5 rounded-full border border-sky-400/30 bg-sky-500/10 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-sky-300 sm:text-sm"
          >
            <Sparkles size={15} className="shrink-0 text-sky-400" />
            {t("marketing.tagline")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/produits"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-8 py-4 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_40px_rgba(56,189,248,0.5)]"
            >
              {t("home.cta1")}
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <a
              href="#pourquoi"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-8 py-4 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-sky-400/60 hover:text-sky-300"
            >
              {t("home.cta2")}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-16 grid max-w-2xl grid-cols-2 gap-6 sm:grid-cols-4"
          >
            {t("home.stats").map((s) => (
              <div key={s.label}>
                <div className="font-mono-tech text-2xl font-semibold text-sky-400">{s.value}</div>
                <div className="mt-1 text-xs leading-snug text-slate-400">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-sky-400/70"
        >
          <ChevronDown size={22} />
        </motion.div>
      </section>

      {/* ── SÉLECTEUR VÉHICULE ─────────────────────────── */}
      <section className="relative z-20 mx-auto -mt-14 max-w-5xl px-4 sm:px-8">
        <Reveal>
          <div data-testid="vehicle-finder" className="glacial-card p-7 shadow-2xl shadow-sky-950/40 sm:p-9">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                <Car size={19} />
              </span>
              <h2 className="font-display text-lg font-bold text-slate-50 sm:text-xl">{t("home.finderTitle")}</h2>
            </div>
            <div className="mt-5">
              <VehicleSelector value={finder} onChange={setFinder} />
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-slate-500">
                {found ? t("home.finderFound", { vehicle: finderVehicle }) : t("home.finderEmpty")}
              </p>
              <button
                data-testid="vehicle-finder-submit"
                onClick={findMatch}
                disabled={!finder.modele}
                className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_26px_rgba(56,189,248,0.45)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("home.finderCta")}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── GALERIE PRODUIT (SET / EAU / CLIENT) ─────────── */}
      <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-8 lg:px-16 lg:pt-28">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("home.showEyebrow")}</p>
          <h2 className="font-display mt-3 max-w-2xl text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t("home.showTitle")}
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            { src: "/images/set-complet.png", caption: t("home.show1"), alt: "Ensemble complet de tapis NordiGuard : deux avant et un arrière d'une pièce" },
            { src: "/images/eau-perle.png", caption: t("home.show2"), alt: "Gouttes d'eau qui perlent en sécurité sur les rainures du tapis NordiGuard" },
            { src: "/images/client-heureux.png", caption: t("home.show3"), alt: "Client heureux au volant avec les tapis NordiGuard installés" },
          ].map((card, i) => (
            <Reveal key={card.src} delay={i * 0.1}>
              <figure data-testid={`showcase-card-${i}`} className="glacial-card group relative overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={card.src}
                    alt={card.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#05080E]/90 via-transparent to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 text-sm font-semibold leading-snug text-slate-100">
                  {card.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── AVANTAGES ────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("home.benefitsEyebrow")}</p>
          <h2 className="font-display mt-3 max-w-2xl text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t("home.benefitsTitle")}
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {t("home.benefits").map((b, i) => {
            const Icon = BENEFIT_ICONS[i];
            return (
              <Reveal key={b.title} delay={i * 0.08}>
                <div data-testid={`benefit-card-${i}`} className="glacial-card group h-full p-7">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_24px_rgba(56,189,248,0.35)]">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-lg font-bold text-slate-50">{b.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{b.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ── AVANT / APRÈS ────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16 lg:pb-32">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("home.baEyebrow")}</p>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              {t("home.baTitle1")} <span className="text-glacier-gradient">{t("home.baTitle2")}</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">{t("home.baText")}</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-300">
              {t("home.baBullets").map((btxt) => (
                <li key={btxt} className="flex items-center gap-3">
                  <ShieldCheck size={16} className="shrink-0 text-sky-400" /> {btxt}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <BeforeAfter
              before="/images/avant-sale.png"
              after="/images/apres-protege.png"
              beforeLabel={t("beforeAfter.before")}
              afterLabel={t("beforeAfter.after")}
            />
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* ── POURQUOI NORDIGUARD (BENTO) ────────────────────── */}
      <section id="pourquoi" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("home.whyEyebrow")}</p>
          <h2 className="font-display mt-3 max-w-3xl text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t("home.whyTitle")}
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7" delay={0.05}>
            <div className="glacial-card group relative h-full min-h-[320px] overflow-hidden">
              <img
                src="/images/fabrication-sur-mesure.png"
                alt="Vue éclatée du tapis NordiGuard : bords finis, clips d'installation, couche PVC imperméable, rainures anti-glisse"
                className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05080E] via-[#05080E]/40 to-transparent" />
              <div className="relative flex h-full flex-col justify-end p-8">
                <ScanLine size={24} className="mb-4 text-sky-400" />
                <h3 className="font-display text-xl font-bold text-slate-50">{t("home.why1t")}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300">{t("home.why1x")}</p>
              </div>
            </div>
          </Reveal>

          <Reveal className="md:col-span-5" delay={0.12}>
            <div className="glacial-card group relative h-full min-h-[320px] overflow-hidden">
              <img
                src="/images/bottes-neige.png"
                alt="Bottes enneigées sur un tapis NordiGuard aux rebords surélevés"
                className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05080E] via-[#05080E]/40 to-transparent" />
              <div className="relative flex h-full flex-col justify-end p-8">
                <h3 className="font-display text-xl font-bold text-slate-50">{t("home.why2t")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{t("home.why2x")}</p>
              </div>
            </div>
          </Reveal>

          {t("home.why3").map((c, i) => {
            const Icon = WHY3_ICONS[i];
            return (
              <Reveal key={c.title} className="md:col-span-4" delay={0.08 * i}>
                <div data-testid={`why-card-${i}`} className="glacial-card h-full p-7">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-lg font-bold text-slate-50">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ── PRODUIT EN VEDETTE ───────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16 lg:pb-32">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("home.featuredEyebrow")}</p>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              {t("home.featuredTitle1")} <span className="text-glacier-gradient">{t("home.featuredTitle2")}</span>
            </h2>
          </div>
          <Link
            to="/produits"
            data-testid="featured-view-all"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-sky-400 transition-colors hover:text-sky-300"
          >
            {t("home.viewAll")}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        {p && (
          <Reveal className="mt-12">
            <div data-testid="featured-product" className="glacial-card grid overflow-hidden lg:grid-cols-2">
              <Link to={`/produit/${p.id}`} data-testid="featured-product-link" className="block overflow-hidden">
                <img
                  src="/images/sieges-avant.png"
                  alt="Ensemble NordiGuard Sur-Mesure 3D installé sur les deux sièges avant"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </Link>
              <div className="flex flex-col justify-center gap-4 p-8 lg:p-12">
                <span className="w-fit rounded-full bg-sky-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-950">
                  {lang === "en" ? p.badge_en || p.badge : p.badge}
                </span>
                <h3 className="font-display text-2xl font-extrabold text-slate-50 sm:text-3xl">NordiGuard {p.name}</h3>
                <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{lang === "en" ? p.short_en || p.short : p.short}</p>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-3xl font-extrabold text-slate-50">{fmtPrice(p.price, lang)}</span>
                  <span className="text-xs text-slate-500">{t("home.perSet")}</span>
                </div>
                <Link
                  to={`/produit/${p.id}`}
                  data-testid="featured-details-cta"
                  className="group mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_26px_rgba(56,189,248,0.45)]"
                >
                  {t("home.discover")}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        )}
      </section>

      {/* ── CTA FINAL ────────────────────────────────────── */}
      <section className="relative overflow-hidden border-t border-sky-500/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(56,189,248,0.12),transparent_65%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 text-center sm:px-8 lg:px-16 lg:py-32">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
              {t("home.finalTitle")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">{t("home.finalText")}</p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/produits"
                data-testid="cta-bottom-models"
                className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-8 py-4 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_40px_rgba(56,189,248,0.5)]"
              >
                {t("home.finalCta1")}
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="cta-bottom-contact"
                className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-8 py-4 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-sky-400/60 hover:text-sky-300"
              >
                {t("home.finalCta2")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
